import { useEffect, useState } from "react";

const KEY = "tow-wizard-v1";
const emptyAddr = (city = "Houston", state = "TX") => ({ street: "", city, state, zip: "", notes: "", lat: null, lng: null });

export const newDraft = () => ({
  token: (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`),
  startedAt: Date.now(),
  step: 0,
  service_id: "",
  pickup: emptyAddr(),
  destination: emptyAddr(),
  needs_destination_help: false,
  show_destination: false,
  vehicle: { year: "", make: "", model: "", color: "", type: "", safe_location: null, special_requirements: "", description: "" },
  customer: { name: "", phone: "", contact_method: "call", email: "", contact_notes: "" },
  consent: false,
  photos: [],
});

export function useWizardDraft() {
  const [draft, setDraft] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(KEY));
      if (saved?.token) return { ...newDraft(), ...saved, step: Math.min(saved.step || 0, 2) };
    } catch (e) { /* ignore corrupted draft */ }
    return newDraft();
  });

  useEffect(() => {
    sessionStorage.setItem(KEY, JSON.stringify(draft));
  }, [draft]);

  const update = (section, patch) =>
    setDraft((d) => (typeof patch === "object" && patch !== null && !Array.isArray(patch) && typeof d[section] === "object" && d[section] !== null && !Array.isArray(d[section])
      ? { ...d, [section]: { ...d[section], ...patch } }
      : { ...d, [section]: patch }));

  const reset = () => setDraft(newDraft());
  return { draft, setDraft, update, reset };
}

export const isTow = (serviceId, settings) => (settings.tow_services || []).includes(serviceId);

export const needsDestination = (d, settings) =>
  isTow(d.service_id, settings) ? !d.needs_destination_help : d.show_destination;

const ZIP = /^\d{5}(-\d{4})?$/;
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/;
export const phoneDigits = (p) => {
  const d = (p || "").replace(/\D/g, "");
  return d.length === 11 && d.startsWith("1") ? d.slice(1) : d;
};

function addrErrors(a, prefix, e) {
  if ((a.street || "").trim().length < 3) e[`${prefix}.street`] = "v.street";
  if ((a.city || "").trim().length < 2) e[`${prefix}.city`] = "v.required";
  if ((a.state || "").trim().length < 2) e[`${prefix}.state`] = "v.required";
  if (!ZIP.test((a.zip || "").trim())) e[`${prefix}.zip`] = "v.zip";
}

export function validateStep(step, d, settings) {
  const e = {};
  if (step === 0 && !d.service_id) e.service_id = "v.service";
  if (step === 1) {
    addrErrors(d.pickup, "pickup", e);
    if (needsDestination(d, settings)) addrErrors(d.destination, "destination", e);
  }
  if (step === 2) {
    if (!d.vehicle.make.trim()) e["vehicle.make"] = "v.required";
    if (!d.vehicle.model.trim()) e["vehicle.model"] = "v.required";
    if (!d.vehicle.type) e["vehicle.type"] = "v.required";
    if (d.vehicle.safe_location === null) e["vehicle.safe_location"] = "v.safe";
    if (d.customer.name.trim().length < 2) e["customer.name"] = "v.name";
    const pd = phoneDigits(d.customer.phone);
    if (pd.length !== 10 || "01".includes(pd[0])) e["customer.phone"] = "v.phone";
    if (d.customer.email.trim() && !EMAIL.test(d.customer.email.trim())) e["customer.email"] = "v.email";
    if (!d.consent) e.consent = "v.consent";
  }
  return e;
}

export function buildPayload(d, settings, lang) {
  const dest = needsDestination(d, settings) ? d.destination : null;
  return {
    service_id: d.service_id,
    pickup: d.pickup,
    destination: dest,
    needs_destination_help: isTow(d.service_id, settings) && d.needs_destination_help,
    vehicle: { ...d.vehicle, year: d.vehicle.year ? Number(d.vehicle.year) : null },
    customer: d.customer,
    photo_ids: d.photos.map((p) => p.id),
    consent: d.consent,
    language: lang,
    client_token: d.token,
    website: "",
    elapsed_ms: Date.now() - d.startedAt,
  };
}
